import { api } from './api'

// --------------- interfaces ---------------

export interface ApiLogEntry {
  id: string
  timestamp: string
  method: string
  path: string
  query_params: string | null
  status_code: number
  response_time_ms: number
  ip_address: string | null
  user_agent: string | null
  error_detail: string | null
  chatbot_id: string | null
  api_key_label: string | null
  api_key_id: string | null
}

export interface ApiLogDetail extends ApiLogEntry {
  request_body: string | null
  response_body: string | null
}

export interface ApiLogListResponse {
  logs: ApiLogEntry[]
  total: number
  page: number
  pages: number
}

export interface ApiLogStats {
  total_requests: number
  success_count: number
  error_count: number
  avg_response_time_ms: number
  requests_by_method: Record<string, number>
  requests_by_hour: Array<{ hour: string; count: number }>
}

export interface ApiLogFilters {
  page?: number
  limit?: number
  method?: string
  status?: string
  path?: string
  api_key_id?: string
  api_key_label?: string
  chatbot_id?: string
  start_date?: string
  end_date?: string
}

// --------------- API functions ---------------

export async function getApiLogs(filters: ApiLogFilters = {}): Promise<ApiLogListResponse> {
  const params = new URLSearchParams()
  if (filters.page) params.set('page', String(filters.page))
  if (filters.limit) params.set('limit', String(filters.limit))
  if (filters.method) params.set('method', filters.method)
  if (filters.status) params.set('status', filters.status)
  if (filters.path) params.set('path', filters.path)
  if (filters.api_key_id) params.set('api_key_id', filters.api_key_id)
  if (filters.api_key_label) params.set('api_key_label', filters.api_key_label)
  if (filters.chatbot_id) params.set('chatbot_id', filters.chatbot_id)
  if (filters.start_date) params.set('start_date', filters.start_date)
  if (filters.end_date) params.set('end_date', filters.end_date)

  const { data } = await api.get<ApiLogListResponse>(`/api-logs?${params.toString()}`)
  return data
}

export async function getApiLogStats(period: string = '24h'): Promise<ApiLogStats> {
  const { data } = await api.get<ApiLogStats>(`/api-logs/stats?period=${period}`)
  return data
}

export async function getApiLogDetail(id: string): Promise<ApiLogDetail> {
  const { data } = await api.get<ApiLogDetail>(`/api-logs/${id}`)
  return data
}

export async function exportApiLogsCsv(filters: ApiLogFilters = {}): Promise<void> {
  const params = new URLSearchParams()
  if (filters.method) params.set('method', filters.method)
  if (filters.status) params.set('status', filters.status)
  if (filters.path) params.set('path', filters.path)
  if (filters.api_key_id) params.set('api_key_id', filters.api_key_id)
  if (filters.api_key_label) params.set('api_key_label', filters.api_key_label)
  if (filters.chatbot_id) params.set('chatbot_id', filters.chatbot_id)
  if (filters.start_date) params.set('start_date', filters.start_date)
  if (filters.end_date) params.set('end_date', filters.end_date)

  const response = await api.get(`/api-logs/export?${params.toString()}`, {
    responseType: 'blob',
  })

  const url = window.URL.createObjectURL(new Blob([response.data]))
  const a = document.createElement('a')
  a.href = url
  a.download = 'api_logs.csv'
  document.body.appendChild(a)
  a.click()
  a.remove()
  window.URL.revokeObjectURL(url)
}
